import React, { useState } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { Home, Compass, Sparkles, Library, Wallet, Mic2, Crown, Shield, Bell, LogOut, User, Search, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Player } from "@/components/Player";
import { PWAInstallBanner } from "@/components/PWAInstallBanner";
import { DownloadModal } from "@/components/DownloadModal";
import { LyricVideoModal } from "@/components/LyricVideoModal";
import { coverFor } from "@/components/common";
import api from "@/lib/api";

const NAV = [
  { to: "/", icon: Home, label: "Home" },
  { to: "/explore", icon: Compass, label: "Explore" },
  { to: "/library", icon: Library, label: "Library" },
  { to: "/wallet", icon: Wallet, label: "Wallet" },
];
const SIDE_EXTRA = [
  { to: "/studio", icon: Sparkles, label: "Create Music" },
  { to: "/voice", icon: Mic2, label: "Voice Studio" },
  { to: "/plans", icon: Crown, label: "Plans" },
];

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
      <img src="/icon-192.png" className="w-9 h-9 rounded-xl" alt="JixxeoX" />
      <span className="text-xl font-black font-display jx-text-gradient">JixxeoX</span>
    </Link>
  );
}

function NotificationsPanel({ open, onClose }) {
  const { notifs, loadNotifs } = useAuth();
  const markRead = async () => { await api.post("/notifications/read"); loadNotifs(); };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose}>
      <div className="absolute right-2 top-16 w-80 max-w-[90vw] jx-glass rounded-2xl p-4 max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="notifications-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Notifications</h3>
          <button onClick={markRead} className="text-xs text-purple-400">Mark all read</button>
        </div>
        {notifs.notifications.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">No notifications yet</p>}
        {notifs.notifications.map((n) => (
          <div key={n.id} className={`p-3 rounded-xl mb-2 ${n.read ? "bg-white/[0.03]" : "bg-purple-500/10 border border-purple-500/20"}`}>
            <p className="text-sm font-medium">{n.title}</p>
            {n.body && <p className="text-xs text-slate-400 mt-0.5">{n.body}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Layout() {
  const { user, logout, notifs } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [notifOpen, setNotifOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dlSong, setDlSong] = useState(null);
  const [lvSong, setLvSong] = useState(null);
  const [q, setQ] = useState("");

  const isActive = (to) => (to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to));
  const goSearch = (e) => { e.preventDefault(); if (q.trim()) navigate(`/explore?q=${encodeURIComponent(q)}`); };

  return (
    <div className="min-h-screen bg-[#09090b] text-slate-100">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col fixed left-0 top-0 bottom-0 w-[260px] jx-glass border-r border-white/10 p-5 z-30">
        <Logo />
        <button onClick={() => navigate("/studio")} data-testid="sidebar-create-btn"
          className="jx-cta text-white font-semibold rounded-xl py-3 mt-6 flex items-center justify-center gap-2">
          <Sparkles size={18} /> Create Music
        </button>
        <nav className="mt-6 space-y-1">
          {[...NAV, ...SIDE_EXTRA].map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive(n.to) ? "bg-white/10 text-white font-semibold" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
              <n.icon size={19} /> {n.label}
            </Link>
          ))}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm ${isActive("/admin") ? "bg-white/10 text-white font-semibold" : "text-amber-400/80 hover:bg-white/5"}`}>
              <Shield size={19} /> Admin
            </Link>
          )}
        </nav>
        <div className="mt-auto">
          {user ? (
            <div className="flex items-center gap-2 p-2 rounded-xl bg-white/5">
              <Link to={`/u/${user.username}`} className="flex items-center gap-2 min-w-0 flex-1">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 grid place-items-center text-xs font-bold overflow-hidden">
                  {user.avatar_url ? <img src={user.avatar_url} className="w-full h-full object-cover" alt="" /> : user.display_name?.[0]}
                </div>
                <div className="min-w-0"><p className="text-xs font-semibold truncate">{user.display_name}</p><p className="text-[10px] text-slate-500 capitalize">{user.plan_key}</p></div>
              </Link>
              <button onClick={logout} data-testid="logout-btn" className="text-slate-500 hover:text-rose-400"><LogOut size={16} /></button>
            </div>
          ) : (
            <Link to="/login" className="block text-center jx-cta text-white rounded-xl py-2.5 text-sm font-semibold">Sign In</Link>
          )}
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 jx-glass border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <Logo />
        <div className="ml-auto flex items-center gap-1">
          {user && (
            <button onClick={() => setNotifOpen(true)} data-testid="notif-btn-mobile" className="relative p-2 text-slate-300">
              <Bell size={20} />
              {notifs.unread > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full" />}
            </button>
          )}
          <button onClick={() => setMenuOpen(true)} className="p-2 text-slate-300"><Menu size={22} /></button>
        </div>
      </header>

      {/* Desktop top bar */}
      <div className="hidden lg:flex fixed top-0 right-0 left-[260px] h-16 z-20 items-center gap-4 px-6 jx-glass border-b border-white/10">
        <form onSubmit={goSearch} className="relative max-w-md w-full">
          <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input-desktop" placeholder="Search songs, creators, genres…"
            className="w-full bg-black/30 border border-white/10 rounded-full pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-purple-500/50" />
        </form>
        <div className="ml-auto flex items-center gap-2">
          {user && (
            <button onClick={() => setNotifOpen(true)} data-testid="notif-btn" className="relative p-2 text-slate-300 hover:text-white">
              <Bell size={20} />
              {notifs.unread > 0 && <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-rose-500 rounded-full text-[10px] grid place-items-center">{notifs.unread}</span>}
            </button>
          )}
          {!user && <Link to="/login" className="jx-cta text-white rounded-full px-5 py-2 text-sm font-semibold">Sign In</Link>}
        </div>
      </div>

      {/* Mobile slide menu */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/60" onClick={() => setMenuOpen(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-72 jx-glass p-5" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setMenuOpen(false)} className="mb-4 text-slate-400"><X /></button>
            {user ? (
              <Link to={`/u/${user.username}`} onClick={() => setMenuOpen(false)} className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 grid place-items-center font-bold overflow-hidden">{user.avatar_url ? <img src={user.avatar_url} className="w-full h-full object-cover" alt="" /> : user.display_name?.[0]}</div>
                <div><p className="font-semibold text-sm">{user.display_name}</p><p className="text-xs text-slate-500 capitalize">{user.plan_key} plan</p></div>
              </Link>
            ) : <Link to="/login" onClick={() => setMenuOpen(false)} className="block jx-cta text-white text-center rounded-xl py-2.5 mb-4 font-semibold">Sign In</Link>}
            {[...SIDE_EXTRA, { to: "/plans", icon: Crown, label: "Plans" }].map((n) => (
              <Link key={n.label} to={n.to} onClick={() => setMenuOpen(false)} className="flex items-center gap-3 py-3 text-slate-300"><n.icon size={19} /> {n.label}</Link>
            ))}
            {user?.role === "admin" && <Link to="/admin" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 py-3 text-amber-400"><Shield size={19} /> Admin</Link>}
            {user && <Link to={`/u/${user.username}`} onClick={() => setMenuOpen(false)} className="flex items-center gap-3 py-3 text-slate-300"><User size={19} /> My Profile</Link>}
            {user && <button onClick={() => { logout(); setMenuOpen(false); }} className="flex items-center gap-3 py-3 text-rose-400"><LogOut size={19} /> Log Out</button>}
          </div>
        </div>
      )}

      {/* Main */}
      <main className="lg:ml-[260px] lg:pt-16 pb-40 lg:pb-32 min-h-screen">
        <Outlet context={{ openDownload: setDlSong, openLyricVideo: setLvSong }} />
      </main>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 jx-glass border-t border-white/10 flex items-center justify-around px-2 safe-bottom" style={{ bottom: 0 }} data-testid="bottom-nav">
        {NAV.slice(0, 2).map((n) => (
          <Link key={n.to} to={n.to} className={`flex flex-col items-center gap-0.5 py-2 px-3 text-[10px] ${isActive(n.to) ? "text-purple-400" : "text-slate-400"}`}><n.icon size={21} />{n.label}</Link>
        ))}
        <button onClick={() => navigate("/studio")} data-testid="bottom-create-btn" className="w-12 h-12 -mt-5 rounded-2xl jx-cta grid place-items-center text-white shadow-lg jx-glow-purple"><Sparkles size={22} /></button>
        {NAV.slice(2).map((n) => (
          <Link key={n.to} to={n.to} className={`flex flex-col items-center gap-0.5 py-2 px-3 text-[10px] ${isActive(n.to) ? "text-purple-400" : "text-slate-400"}`}><n.icon size={21} />{n.label}</Link>
        ))}
      </nav>

      <Player onDownload={setDlSong} onLyricVideo={setLvSong} />
      <PWAInstallBanner />
      <NotificationsPanel open={notifOpen} onClose={() => setNotifOpen(false)} />
      <DownloadModal open={!!dlSong} song={dlSong} onClose={() => setDlSong(null)} />
      <LyricVideoModal open={!!lvSong} song={lvSong} onClose={() => setLvSong(null)} />
    </div>
  );
}
